import React from "react";
import { motion } from "framer-motion";
import {
  Github,
  Linkedin,
  Twitter,
  Mail,
  Instagram,
  Youtube,
  Facebook,
  ExternalLink,
} from "lucide-react";

const SocialLinks = ({ variant = "default", size = "md" }) => {
  const socialData = [
    {
      name: "GitHub",
      url: "https://github.com/t-boye",
      icon: Github,
      color: "#181717",
      hoverColor: "#10b981",
      description: "Check out my open source projects and contributions",
    },
    {
      name: "LinkedIn",
      url: "https://linkedin.com/in/tboye",
      icon: Linkedin,
      color: "#0A66C2",
      hoverColor: "#0A66C2",
      description: "Connect with me professionally",
    },
    // {
    //   name: "Twitter",
    //   url: "https://twitter.com/yourusername",
    //   icon: Twitter,
    //   color: "#1DA1F2",
    //   hoverColor: "#1DA1F2",
    //   description: "Follow me for tech insights and updates",
    // },
    {
      name: "Instagram",
      url: "https://instagram.com/tboye.official",
      icon: Instagram,
      color: "#E4405F",
      hoverColor: "#E4405F",
      description: "Behind the scenes and creative process",
    },
    {
      name: "YouTube",
      url: "https://www.youtube.com/@tboyex",
      icon: Youtube,
      color: "#FF0000",
      hoverColor: "#FF0000",
      description: "Coding tutorials and tech reviews",
    },
    {
      name: "Email",
      url: "mailto:emmanuelboye1957@gmail.com",
      icon: Mail,
      color: "#EA4335",
      hoverColor: "#EA4335",
      description: "Send me a direct message",
    },
  ];

  const sizeClasses = {
    sm: "w-10 h-10",
    md: "w-12 h-12",
    lg: "w-16 h-16",
  };

  const iconSizes = {
    sm: 18,
    md: 20,
    lg: 24,
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.8 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
      },
    },
  };

  if (variant === "minimal") {
    return (
      <motion.div
        className="flex space-x-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {socialData.slice(0, 4).map((social) => {
          const Icon = social.icon;
          return (
            <motion.a
              key={social.name}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 dark:text-gray-400 hover:text-primary-500 transition-colors"
              variants={itemVariants}
              whileHover={{ scale: 1.2, y: -2 }}
              whileTap={{ scale: 0.9 }}
              title={social.name}
            >
              <Icon size={iconSizes[size]} />
            </motion.a>
          );
        })}
      </motion.div>
    );
  }

  if (variant === "cards") {
    return (
      <motion.div
        className="grid grid-cols-2 md:grid-cols-3 gap-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {socialData.map((social) => {
          const Icon = social.icon;
          return (
            <motion.a
              key={social.name}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 bg-white dark:bg-dark-900 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 group border border-gray-200 dark:border-gray-700"
              variants={itemVariants}
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center space-x-3 mb-2">
                <div
                  className="p-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: `${social.color}15`,
                    color: social.color,
                  }}
                >
                  <Icon size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    {social.name}
                  </h4>
                </div>
                <ExternalLink
                  size={14}
                  className="text-gray-400 group-hover:text-primary-500 transition-colors ml-auto"
                />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors">
                {social.description}
              </p>
            </motion.a>
          );
        })}
      </motion.div>
    );
  }

  // Default variant - circular buttons
  return (
    <motion.div
      className="flex flex-wrap gap-4"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {socialData.map((social) => {
        const Icon = social.icon;
        return (
          <motion.a
            key={social.name}
            href={social.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`${sizeClasses[size]} rounded-full bg-white dark:bg-dark-900 shadow-md hover:shadow-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center transition-all duration-300 group overflow-hidden relative`}
            variants={itemVariants}
            whileHover={{
              scale: 1.1,
              y: -3,
              rotate: [0, -5, 5, 0],
              transition: { duration: 0.3 },
            }}
            whileTap={{ scale: 0.9 }}
            title={social.description}
          >
            {/* Background color on hover */}
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300"
              style={{ backgroundColor: social.color }}
            />

            {/* Icon */}
            <Icon
              size={iconSizes[size]}
              className="text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors relative z-10"
              style={{
                color: "inherit",
              }}
            />

            {/* Hover effect ring */}
            <div
              className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 border-2"
              style={{ borderColor: social.hoverColor }}
            />
          </motion.a>
        );
      })}
    </motion.div>
  );
};

export default SocialLinks;
